import * as React from "react";
import { Provider } from "react-redux";

import { Form } from "../../../../../Engine/Form/Form";
import { FormSettings } from "../../../../../Forms/Types/FormSettings";
import { FormObjectProvider } from "../../../../FormObjectProvider";
import { LocalizerProvider } from "../../../../../Engine/Core/Localization/LocalizerContext";
import { initialModelToInner } from "../../../../../Forms/FormInitialization/DataFetching";
import { useAsyncEffect } from "../../../../../../../Common/UseAsyncEffect";
import { DataDeclarationFactory, ValidationFunctionsFactory } from "../../../../../Forms/RawFormResources";
import { candyInjectableModules } from "../../../../../Forms/AllModules";
import { iterateModelEntries } from "../../../../viewModelUtils";
import { arrayHasLength, ensureNotNullOrUndefined, trimLastSlash } from "../../../../../../../Common/TypingUtils";
import { PathTokens } from "../../../../../../../Common/ModelPath/ModelPath";
import { DraftFullId } from "../../../../../../../Common/CandyApiClient/Types/DraftFullId";
import { ComponentHandleRegistryProvider } from "../../../../../Engine/Core/ComponentHandleRegistry/ComponentHandleRegistryContext";
import { AutocalculationConfigurator } from "../../../../../Forms/FormResources";
import { loadCalculator2Resources } from "../../../../../Forms/FormInitialization/LoadCalculator2Resources";

interface InlineFormProps {
    parentDraftId: DraftFullId;
    parentKeformsUrl: string;
    initialData: { [path: string]: string };
    validationFunctions: ValidationFunctionsFactory;
    kcXmlValidationConfigurator: AutocalculationConfigurator;
    dataDeclaration: DataDeclarationFactory;
    children: React.ReactNode;
}

export interface InlineFormHandler {
    getFormData(): { [path: string]: string };
    isFormDataValid(): boolean;
    focusFirstInvalidControl(): Promise<boolean>;
}

export const InlineForm = React.forwardRef((props: InlineFormProps, ref: React.Ref<InlineFormHandler>) => {
    const [form, setForm] = React.useState<IForm | undefined>(undefined);
    useAsyncEffect(async () => {
        const settings = createInlineFormSettings(props.parentDraftId, props.parentKeformsUrl);
        const formObject = new Form(
            {
                calculationHooks: {},
                calculationFunctions: {},
                dataDeclaration: props.dataDeclaration(candyInjectableModules),
                validationFunctions: props.validationFunctions(candyInjectableModules),
            },
            settings,
            [],
            "NO_HASH"
        );
        loadCalculator2Resources(formObject, [props.kcXmlValidationConfigurator]);
        await formObject.performInitialFormLoad(initialModelToInner(props.initialData), settings);
        setForm(formObject);
    }, []);

    React.useImperativeHandle(
        ref,
        () => ({
            getFormData: () => {
                ensureNotNullOrUndefined(form);
                const formViewModel = form.store.getState().viewModel;
                const result: { [path: string]: string } = {};
                for (const [path, viewModel] of iterateModelEntries(formViewModel)) {
                    const pathPartsAsArray = path.getPathPartsAsArray();
                    if (!arrayHasLength(pathPartsAsArray, 1) || !PathTokens.isSimpleToken(pathPartsAsArray[0])) {
                        throw new Error("Сложные пути не поддреживаются в формах пользовательких справочников");
                    }
                    if (viewModel.value != undefined) {
                        result[pathPartsAsArray[0]] = viewModel.value;
                    }
                }
                return result;
            },
            isFormDataValid: () => {
                ensureNotNullOrUndefined(form);
                const errorCount = form.getViewModelField(form.settings.formRoot, "errorsCount");
                return errorCount == undefined || errorCount === 0 || errorCount === "0";
            },
            focusFirstInvalidControl: () => {
                ensureNotNullOrUndefined(form);
                return form.focusFirstInvalidControl();
            },
        }),
        [form]
    );

    if (form == undefined) {
        return <>Loading...</>;
    }

    return (
        <FormObjectProvider form={form}>
            <ComponentHandleRegistryProvider value={form.componentHandleRegistry}>
                <LocalizerProvider localizer={form.localization}>
                    <Provider store={form.store}>{props.children}</Provider>
                </LocalizerProvider>
            </ComponentHandleRegistryProvider>
        </FormObjectProvider>
    );
});
InlineForm.displayName = "InlineForm";

declare const global: Window;

function createInlineFormSettings(dratId: DraftFullId, keformsUrl: string): FormSettings {
    return {
        embeddedLogEnabled: false,
        useExtendedErrorLog: false,
        allowsend: false,
        printInfo: {},
        showFormContainer: true,
        regions: [],
        formRoot: "Root",
        keformsUrl: trimLastSlash(global.CF.keformsUrl ?? keformsUrl),
        staffServiceUrl: trimLastSlash(global.CF.staffServiceUrl),
        picklistUrl: trimLastSlash(global.CF.picklistUrl),
        nidusUrl: trimLastSlash(global.CF.nidusUrl),
        fsPrinterUrl: trimLastSlash(global.CF.fsPrinterUrl),
        contractorsApiUrl: trimLastSlash(global.CF.contractorsApiUrl),
        draftId: dratId.draftId,
        ns: dratId.ns,
        gfv: "NONE",
    };
}
